import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sprout, HeartHandshake, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import BrandMarquee from "@/components/BrandMarquee";
import { Reveal } from "@/lib/motion";
import { IMAGES } from "@/lib/media";

const values = [
  { icon: Sprout, t: "Stagionalità", d: "Seguiamo il ritmo della natura: acquistiamo ciò che è davvero di stagione, al massimo del sapore." },
  { icon: HeartHandshake, t: "Rapporto diretto", d: "Nessun intermediario superfluo. Parliamo ogni giorno con chef, cuochi e responsabili acquisti." },
  { icon: ShieldCheck, t: "Affidabilità", d: "Consegne puntuali e qualità costante: la tua cucina non si ferma mai." },
];

const stats = [
  { n: "24h", l: "Consegna dall'ordine" },
  { n: "VR", l: "Verona e provincia" },
  { n: "100%", l: "Prodotto controllato" },
];

export default function ChiSiamo() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <main data-testid="chi-siamo-page">
      <PageHero
        eyebrow="Chi Siamo"
        title="Persone,"
        italic="prima che prodotti."
        subtitle="B FRUIT SRL è una piccola azienda di distribuzione di frutta e verdura fresca. Serviamo ristoranti, alberghi, hotel e piccoli supermercati con la cura di chi conosce ogni cliente per nome."
      />

      <section ref={ref} className="mx-auto max-w-[1400px] px-4 sm:px-8 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="arch overflow-hidden shadow-xl">
            <motion.img style={{ y }} src={IMAGES.marketVeg} alt="Selezione di verdura al mercato" className="w-full h-[68vh] object-cover" />
          </div>
        </div>
        <div className="lg:col-span-6">
          <Reveal>
            <h2 className="font-serif-display text-4xl sm:text-6xl tracking-tight text-[#1A3322] leading-[0.98]">
              Dal mercato alla tua cucina, <span className="italic font-light text-[#C85A32]">senza compromessi.</span>
            </h2>
            <div className="mt-8 space-y-6 text-lg leading-relaxed text-[#4A5A4E]">
              <p>
                Ogni giornata inizia prima dell'alba, tra le casse del mercato ortofrutticolo. Scegliamo
                a mano frutta e verdura, valutando maturazione, colore e provenienza.
              </p>
              <p>
                Siamo una realtà a misura di persona: questo ci permette di essere flessibili, veloci e
                attenti alle esigenze di ogni singolo cliente, dal ristorante stellato al supermercato di quartiere.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {values.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.1}>
              <div className="group border-t border-[#1A3322]/15 pt-7">
                <v.icon className="h-9 w-9 text-[#C85A32] transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-5 font-serif-display text-3xl text-[#1A3322]">{v.t}</h3>
                <p className="mt-3 text-[#4A5A4E] leading-relaxed">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <BrandMarquee dark />

      <section className="bg-[#EAE7E1]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-20 lg:py-28 grid grid-cols-2 lg:grid-cols-3 gap-10">
          {stats.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08}>
              <div>
                <p className="font-serif-display text-6xl lg:text-7xl text-[#1A3322]">{s.n}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#4A5A4E]">{s.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-32 text-center">
        <Reveal>
          <h2 className="font-serif-display text-4xl sm:text-6xl tracking-tight text-[#1A3322] max-w-3xl mx-auto">
            Vuoi conoscerci meglio? <span className="italic font-light text-[#C85A32]">Scrivici.</span>
          </h2>
          <Link to="/contatti" className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#1A3322] text-[#F4F2EE] px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#C85A32] transition-colors duration-300">
            Contattaci <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
